/* =============================================================
   ConsultationPage — United Dream Mongolia
   2-step flow: 1) Select consultation type → 2) Enter name & phone
   ============================================================= */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "wouter";
import { ArrowLeft, ArrowRight, CheckCircle2, Phone, User, MessageSquare } from "lucide-react";

const COUNTRY_NAMES: Record<string, string> = {
  usa: "АНУ 🇺🇸",
  australia: "Австрали 🇦🇺",
  canada: "Канад 🇨🇦",
  ireland: "Ирланд 🇮🇪",
  czech: "Чех Улс 🇨🇿",
  schengen: "Шенген 🇪🇺",
  "new-zealand": "Шинэ Зеланд 🇳🇿",
};

const CONSULTATION_TYPES = [
  {
    id: "general",
    title: "Ерөнхий зөвлөгөө",
    desc: "Аль улсад, ямар визийн төрөл тохиромжтойг тодорхойлох",
    duration: "30 мин",
    price: "Үнэгүй",
    priceColor: "oklch(0.72 0.20 150)",
    icon: "💬",
  },
  {
    id: "travel",
    title: "Аяллын виз зөвлөгөө",
    desc: "Аяллын визийн бүрдүүлэлт, магадлал, стратеги",
    duration: "45 мин",
    price: "₮49,000",
    priceColor: "oklch(0.72 0.20 295)",
    icon: "✈️",
  },
  {
    id: "study",
    title: "Сургалтын виз зөвлөгөө",
    desc: "Сургуулийн сонголт, сургалтын визийн бүрдүүлэлт",
    duration: "45 мин",
    price: "₮49,000",
    priceColor: "oklch(0.72 0.20 295)",
    icon: "🎓",
  },
  {
    id: "full",
    title: "Иж бүрэн стратеги",
    desc: "Ажил, цагаачлал, ногоон карт — бүх боломжийг нарийвчлан судлах",
    duration: "60 мин",
    price: "₮99,000",
    priceColor: "oklch(0.75 0.22 40)",
    icon: "🌟",
  },
];

export default function ConsultationPage() {
  const [location] = useLocation();
  const countrySlug = location.split("/consultation/")[1] || "";
  const countryName = COUNTRY_NAMES[countrySlug] || "";

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [, navigate] = useLocation();

  const selectedConsult = CONSULTATION_TYPES.find((t) => t.id === selectedType);

  const handleSubmit = () => {
    if (!name.trim() || !phone.trim()) return;
    setStep(3);
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "oklch(0.10 0.06 285)" }}
    >
      {/* Background blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl"
          style={{ background: "oklch(0.55 0.28 295)" }} />
        <div className="absolute bottom-20 right-1/4 w-80 h-80 rounded-full opacity-8 blur-3xl"
          style={{ background: "oklch(0.52 0.26 285)" }} />
      </div>

      {/* Header */}
      <div
        className="sticky top-0 z-20 flex items-center gap-3 px-5 py-4 border-b"
        style={{
          background: "oklch(0.10 0.06 285 / 0.95)",
          borderColor: "oklch(0.55 0.28 295 / 0.15)",
          backdropFilter: "blur(12px)",
        }}
      >
        <button
          onClick={() => (step === 1 ? navigate("/") : setStep(1))}
          className="flex items-center gap-2 text-sm transition-opacity hover:opacity-70"
          style={{ color: "oklch(0.70 0.08 285)" }}
        >
          <ArrowLeft className="w-4 h-4" />
          {step === 1 ? "Буцах" : "Төрөл солих"}
        </button>
        <div className="flex-1" />
        {countryName && (
          <span className="text-sm font-semibold" style={{ color: "oklch(0.80 0.10 285)" }}>
            {countryName}
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div className="w-full h-1" style={{ background: "oklch(0.55 0.28 295 / 0.15)" }}>
        <motion.div
          className="h-full rounded-full"
          style={{ background: "linear-gradient(90deg, oklch(0.72 0.22 295), oklch(0.60 0.26 285))" }}
          animate={{ width: step === 1 ? "33%" : step === 2 ? "66%" : "100%" }}
          transition={{ duration: 0.4 }}
        />
      </div>

      <div className="relative z-10 flex-1 px-4 py-8 max-w-xl mx-auto w-full">
        <AnimatePresence mode="wait">

          {/* ── STEP 1: Төрөл сонгох ─────────────────────────── */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.3 }}
            >
              <div className="mb-8">
                <p className="text-xs font-semibold tracking-widest uppercase mb-2"
                  style={{ color: "oklch(0.72 0.20 295)" }}>
                  Алхам 1 / 2
                </p>
                <h1
                  className="text-2xl md:text-3xl font-bold mb-2"
                  style={{ color: "oklch(0.97 0.01 285)", fontFamily: "'Sora', sans-serif" }}
                >
                  Зөвлөгөөний төрөл сонгоно уу
                </h1>
                <p className="text-sm" style={{ color: "oklch(0.65 0.06 285)" }}>
                  Таны хэрэгцээнд тохирсон зөвлөгөөний төрлийг сонгоно уу
                </p>
              </div>

              <div className="flex flex-col gap-3">
                {CONSULTATION_TYPES.map((type, i) => (
                  <motion.button
                    key={type.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.07 }}
                    onClick={() => {
                      setSelectedType(type.id);
                      setStep(2);
                    }}
                    className="group w-full text-left p-5 rounded-2xl border transition-all duration-200 hover:-translate-y-0.5"
                    style={{
                      background: "oklch(0.55 0.28 295 / 0.07)",
                      borderColor: "oklch(0.55 0.28 295 / 0.20)",
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
                        style={{ background: "oklch(0.55 0.28 295 / 0.15)" }}
                      >
                        {type.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="font-bold text-base" style={{ color: "oklch(0.97 0.01 285)" }}>
                            {type.title}
                          </span>
                          <span className="text-sm font-bold flex-shrink-0" style={{ color: type.priceColor }}>
                            {type.price}
                          </span>
                        </div>
                        <p className="text-sm leading-relaxed" style={{ color: "oklch(0.65 0.06 285)" }}>
                          {type.desc}
                        </p>
                        <span
                          className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full"
                          style={{
                            background: "oklch(0.55 0.28 295 / 0.15)",
                            color: "oklch(0.72 0.18 295)",
                          }}
                        >
                          ⏱ {type.duration}
                        </span>
                      </div>
                      <ArrowRight
                        className="w-5 h-5 flex-shrink-0 opacity-30 group-hover:opacity-70 transition-opacity"
                        style={{ color: "oklch(0.72 0.20 295)" }}
                      />
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* ── STEP 2: Нэр, утас ────────────────────────────── */}
          {step === 2 && selectedConsult && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.3 }}
            >
              <div className="mb-6">
                <p className="text-xs font-semibold tracking-widest uppercase mb-2"
                  style={{ color: "oklch(0.72 0.20 295)" }}>
                  Алхам 2 / 2
                </p>
                <h1
                  className="text-2xl md:text-3xl font-bold mb-2"
                  style={{ color: "oklch(0.97 0.01 285)", fontFamily: "'Sora', sans-serif" }}
                >
                  Мэдээллээ оруулна уу
                </h1>
                <p className="text-sm" style={{ color: "oklch(0.65 0.06 285)" }}>
                  Бид тантай удахгүй холбогдох болно
                </p>
              </div>

              {/* Selected type summary */}
              <div
                className="flex items-center gap-3 p-4 rounded-2xl border mb-6"
                style={{
                  background: "oklch(0.55 0.28 295 / 0.10)",
                  borderColor: "oklch(0.55 0.28 295 / 0.30)",
                }}
              >
                <span className="text-2xl">{selectedConsult.icon}</span>
                <div className="flex-1">
                  <div className="font-semibold text-sm" style={{ color: "oklch(0.95 0.02 285)" }}>
                    {selectedConsult.title}
                  </div>
                  <div className="text-xs" style={{ color: "oklch(0.65 0.06 285)" }}>
                    {selectedConsult.duration} · {selectedConsult.price}
                  </div>
                </div>
                <button
                  onClick={() => setStep(1)}
                  className="text-xs px-3 py-1 rounded-lg transition-opacity hover:opacity-70"
                  style={{
                    background: "oklch(0.55 0.28 295 / 0.20)",
                    color: "oklch(0.72 0.18 295)",
                  }}
                >
                  Солих
                </button>
              </div>

              {/* Form */}
              <div className="flex flex-col gap-4">
                {/* Name */}
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-semibold mb-2"
                    style={{ color: "oklch(0.80 0.06 285)" }}>
                    <User className="w-4 h-4" />
                    Таны нэр <span style={{ color: "oklch(0.65 0.20 295)" }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Жишээ: Б. Болд"
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                    style={{
                      background: "oklch(0.55 0.28 295 / 0.08)",
                      border: "1.5px solid oklch(0.55 0.28 295 / 0.25)",
                      color: "oklch(0.97 0.01 285)",
                    }}
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-semibold mb-2"
                    style={{ color: "oklch(0.80 0.06 285)" }}>
                    <Phone className="w-4 h-4" />
                    Утасны дугаар <span style={{ color: "oklch(0.65 0.20 295)" }}>*</span>
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="9911 2233"
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                    style={{
                      background: "oklch(0.55 0.28 295 / 0.08)",
                      border: "1.5px solid oklch(0.55 0.28 295 / 0.25)",
                      color: "oklch(0.97 0.01 285)",
                    }}
                  />
                </div>

                {/* Note */}
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-semibold mb-2"
                    style={{ color: "oklch(0.80 0.06 285)" }}>
                    <MessageSquare className="w-4 h-4" />
                    Нэмэлт тэмдэглэл{" "}
                    <span style={{ color: "oklch(0.55 0.06 285)", fontWeight: 400 }}>(заавал биш)</span>
                  </label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Асуух зүйл, тусгай хүсэлт..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none resize-none"
                    style={{
                      background: "oklch(0.55 0.28 295 / 0.08)",
                      border: "1.5px solid oklch(0.55 0.28 295 / 0.25)",
                      color: "oklch(0.97 0.01 285)",
                    }}
                  />
                </div>

                {/* Submit */}
                <motion.button
                  whileHover={name.trim() && phone.trim() ? { scale: 1.02 } : {}}
                  whileTap={name.trim() && phone.trim() ? { scale: 0.98 } : {}}
                  onClick={handleSubmit}
                  disabled={!name.trim() || !phone.trim()}
                  className="w-full py-4 rounded-2xl font-bold text-base mt-2"
                  style={{
                    background: name.trim() && phone.trim()
                      ? "linear-gradient(135deg, oklch(0.62 0.28 295), oklch(0.52 0.26 285))"
                      : "oklch(0.25 0.04 285)",
                    color: name.trim() && phone.trim() ? "white" : "oklch(0.45 0.04 285)",
                    cursor: name.trim() && phone.trim() ? "pointer" : "not-allowed",
                    transition: "all 0.2s",
                  }}
                >
                  Захиалга илгээх
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* ── STEP 3: Амжилттай ────────────────────────────── */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="flex flex-col items-center text-center py-16"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.1 }}
                className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
                style={{ background: "oklch(0.55 0.28 295 / 0.20)" }}
              >
                <CheckCircle2 className="w-10 h-10" style={{ color: "oklch(0.72 0.20 295)" }} />
              </motion.div>
              <h2
                className="text-2xl font-bold mb-3"
                style={{ color: "oklch(0.97 0.01 285)", fontFamily: "'Sora', sans-serif" }}
              >
                Захиалга амжилттай!
              </h2>
              <p className="text-sm mb-2 max-w-xs" style={{ color: "oklch(0.70 0.06 285)" }}>
                <span className="font-semibold" style={{ color: "oklch(0.85 0.08 285)" }}>{name}</span> таны захиалгыг хүлээн авлаа.
              </p>
              <p className="text-sm mb-8 max-w-xs" style={{ color: "oklch(0.65 0.06 285)" }}>
                Бид{" "}
                <span className="font-semibold" style={{ color: "oklch(0.80 0.10 285)" }}>{phone}</span>{" "}
                дугаарт удахгүй холбогдох болно.
              </p>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate("/")}
                className="px-8 py-3 rounded-2xl font-semibold text-sm"
                style={{
                  background: "oklch(0.55 0.28 295 / 0.20)",
                  border: "1px solid oklch(0.55 0.28 295 / 0.35)",
                  color: "oklch(0.80 0.12 295)",
                }}
              >
                Нүүр хуудас руу буцах
              </motion.button>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
}
